import { SignUpButton } from '@clerk/clerk-react';
import {
  ArrowPathRoundedSquareIcon,
  ArrowRightIcon,
  CheckIcon,
  ClipboardDocumentCheckIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Surface } from './ui/Surface';

const benefits = [
  {
    eyebrow: 'League-aware',
    title: 'Answers that fit your format',
    detail: 'Standard, Superflex, 2QB, Best Ball, and Dynasty values stay tied to the league you actually play.',
    accent: 'gold' as const,
  },
  {
    eyebrow: 'Roster-aware',
    title: 'Advice that knows your team',
    detail: 'Import from Sleeper so trades, weekly notes, and draft recommendations start with the players you own.',
    accent: 'cyan' as const,
  },
  {
    eyebrow: 'Season-aware',
    title: 'One desk from draft to playoffs',
    detail: 'Move from rankings to trade calls, mock drafts, and live roster notes without rebuilding your context.',
    accent: 'pink' as const,
  },
];

const steps = [
  ['01', 'Set your league', 'Choose the format and scoring rules that shape every answer.'],
  ['02', 'Bring your roster', 'Connect Sleeper or build your team by hand in a few minutes.'],
  ['03', 'Make the call', 'Open one desk for ranks, trades, drafts, and weekly decisions.'],
];

export function PublicLanding() {
  return (
    <div className="relative min-h-screen overflow-hidden bg-ink">
      <Helmet>
        <title>Fantasy Fiesta, your fantasy football decision desk</title>
        <meta
          name="description"
          content="Bring your league, roster, rankings, trades, drafts, and weekly decisions into one fantasy football desk."
        />
        <link rel="canonical" href="https://fantasy-fiesta.com/" />
        <meta property="og:title" content="Fantasy Fiesta, your fantasy football decision desk" />
        <meta
          property="og:description"
          content="Your league settings, your roster, and every decision in one place."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://fantasy-fiesta.com/" />
        <meta property="og:image" content="https://fantasy-fiesta.com/images/og-share-v3.png" />
        <meta property="og:image:secure_url" content="https://fantasy-fiesta.com/images/og-share-v3.png" />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Fantasy Fiesta: Run your fantasy team without the tab chaos." />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Fantasy Fiesta, your fantasy football decision desk" />
        <meta
          name="twitter:description"
          content="Your league settings, your roster, and every decision in one place."
        />
        <meta name="twitter:image" content="https://fantasy-fiesta.com/images/og-share-v3.png" />
        <meta name="twitter:image:alt" content="Fantasy Fiesta: Run your fantasy team without the tab chaos." />
      </Helmet>

      <div className="pointer-events-none absolute inset-0 stadium-glow" />
      <div className="pointer-events-none absolute inset-0 stadium-grid opacity-40" />
      <div className="pointer-events-none absolute left-1/2 top-20 h-80 w-80 -translate-x-1/2 rounded-full bg-fiesta-yellow/5 blur-3xl" />

      <div className="relative z-10 mx-auto max-w-7xl px-4 pb-20 pt-12 sm:px-6 sm:pt-16 lg:px-8 lg:pb-28 lg:pt-20">
        <section className="grid items-center gap-12 lg:grid-cols-[1.02fr_0.98fr] lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
          >
            <h1 className="max-w-3xl font-display text-5xl font-semibold tracking-tight text-white sm:text-6xl lg:text-7xl text-balance">
              Run your fantasy team without the tab chaos.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-zinc-400 sm:text-xl">
              Fantasy Fiesta brings your league settings, roster, rankings, trades, draft room, and weekly news into one decision desk.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <SignUpButton mode="modal">
                <button className="group inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-fiesta-yellow px-6 py-3 text-base font-semibold text-ink transition-transform hover:scale-[1.02]">
                  Create your fantasy desk
                  <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </button>
              </SignUpButton>
              <Link
                to="/players"
                className="inline-flex min-h-12 items-center justify-center rounded-full border border-white/15 px-6 py-3 text-base font-medium text-white transition-colors hover:border-white/30 hover:bg-white/5"
              >
                Explore the rankings
              </Link>
            </div>

            <div className="mt-7 flex flex-wrap gap-x-5 gap-y-2 text-sm text-zinc-500">
              {['Sleeper import', 'Five league formats', 'Live roster notes'].map((item) => (
                <span key={item} className="inline-flex items-center gap-1.5">
                  <CheckIcon className="h-4 w-4 text-fiesta-blue" />
                  {item}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.08 }}
            className="relative"
          >
            <div className="decision-constellation" aria-label="Your league context connects weekly notes, trades, and draft recommendations">
              <div className="decision-orbit decision-orbit-one" />
              <div className="decision-orbit decision-orbit-two" />
              <span className="decision-path decision-path-one" />
              <span className="decision-path decision-path-two" />
              <span className="decision-path decision-path-three" />

              <div className="decision-heading">
                <h2>Every decision<br />shares context.</h2>
              </div>

              <DecisionNode
                className="decision-node-weekly"
                icon={<ClipboardDocumentCheckIcon />}
                label="Weekly notes"
                title="Start, sit, hold"
                tone="yellow"
              />
              <DecisionNode
                className="decision-node-trade"
                icon={<ArrowPathRoundedSquareIcon />}
                label="Trade desk"
                title="See what changes"
                tone="cyan"
              />
              <DecisionNode
                className="decision-node-draft"
                icon={<SparklesIcon />}
                label="Draft room"
                title="Make the pick fit"
                tone="pink"
              />

              <div className="decision-hub">
                <span className="decision-hub-pulse" />
                <span className="decision-hub-mark">FF</span>
                <span className="decision-hub-copy">
                  <strong>League context</strong>
                  <small>Always in sync</small>
                </span>
              </div>
            </div>
          </motion.div>
        </section>

        <section className="mt-16 grid gap-4 sm:grid-cols-3 lg:mt-24" aria-label="Why join Fantasy Fiesta">
          {benefits.map((benefit, index) => (
            <motion.div
              key={benefit.title}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.15 + index * 0.06 }}
            >
              <Surface className="h-full p-5 sm:p-6" glow={benefit.accent}>
                <p className={`text-xs font-medium uppercase tracking-[0.16em] ${
                  benefit.accent === 'gold'
                    ? 'text-fiesta-yellow'
                    : benefit.accent === 'cyan'
                      ? 'text-fiesta-blue'
                      : 'text-fiesta-pink'
                }`}>
                  {benefit.eyebrow}
                </p>
                <h2 className="mt-3 font-display text-xl font-semibold text-white">{benefit.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-zinc-400">{benefit.detail}</p>
              </Surface>
            </motion.div>
          ))}
        </section>

        <section id="how-it-works" className="mt-20 lg:mt-28">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-sm font-medium text-fiesta-blue">Three steps. One desk.</p>
            <h2 className="mt-3 font-display text-3xl font-semibold text-white sm:text-4xl text-balance">
              Bring the context once. Use it all season.
            </h2>
          </div>
          <div className="mt-10 grid gap-8 md:grid-cols-3">
            {steps.map(([number, title, detail]) => (
              <div key={number} className="relative border-l border-white/10 pl-5">
                <span className="font-display text-3xl font-semibold text-white/15">{number}</span>
                <h3 className="mt-3 text-lg font-semibold text-white">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-400">{detail}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-20 lg:mt-28">
          <Surface className="overflow-hidden px-6 py-12 text-center sm:px-10 sm:py-14" glow="gold">
            <p className="text-sm font-medium text-fiesta-yellow">Your next decision starts here</p>
            <h2 className="mx-auto mt-3 max-w-2xl font-display text-3xl font-semibold text-white sm:text-4xl text-balance">
              Give every rank, trade, and pick the context it deserves.
            </h2>
            <SignUpButton mode="modal">
              <button className="fiesta-gradient-cta group mt-7 inline-flex min-h-12 items-center justify-center gap-2 rounded-full px-6 py-3 font-semibold transition-transform hover:scale-[1.03]">
                <span className="relative z-10">Join Fantasy Fiesta</span>
                <ArrowRightIcon className="relative z-10 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </SignUpButton>
          </Surface>
        </section>
      </div>
    </div>
  );
}

function DecisionNode({
  icon,
  label,
  title,
  tone,
  className = '',
}: {
  icon: React.ReactNode;
  label: string;
  title: string;
  tone: 'yellow' | 'cyan' | 'pink';
  className?: string;
}) {
  return (
    <div className={`decision-node decision-node-${tone} ${className}`}>
      <span className="decision-node-icon">{icon}</span>
      <span>
        <small>{label}</small>
        <strong>{title}</strong>
      </span>
    </div>
  );
}
